import * as React from 'react'
import dynamic from 'next/dynamic'
import styled, { createGlobalStyle, css, ThemeProvider } from 'styled-components'

import * as header from '@ulule/header'
import * as hooks from '@owl-nest/hooks'
import * as layout from '@owl-nest/layout'
import { t } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import { ConfiguredNavMenu, Screen } from '@owl-nest/project-manager-nav'
import * as plume from '@ulule/owl-kit-components/next'
import * as services from '@owl-nest/services'
import * as shadow from '@owl-nest/shadow'
const PreviewBar = dynamic(() => import('@owl-nest/layout').then((mod) => mod.PreviewBar), { ssr: false })

import { DisplayMode } from '../context'
import { useDisplayMode } from '../hooks/useDisplayMode'

import { InnerFooter } from './InnerFooter'
import { getCurrentLocale } from '../router'
import { SEO } from '../SEO'
import * as UFE from '../UFE'

type AppBackboneProps = {
  children: React.ReactNode
  project: models.project.Project
  stickyUntil?: number
}

type Theme = {
  colors: {
    accent: string
    background: string
    hover: string
    primary: string
  }
}

export function AppBackbone({ children, project, stickyUntil }: AppBackboneProps): React.ReactElement {
  const router = hooks.useNextRouter()

  const asPath = router.asPath
  const urlParams = new URLSearchParams(asPath.split('?')[1])
  const prev = urlParams.get('prev')

  const currentLocale = getCurrentLocale(router)
  plume.accounting.settings.number = plume.accounting.NUMBER_FORMAT_CONFIG[currentLocale]

  const auth = services.user.useAuth()
  const isUserStaff = auth.type === 'loggedin' ? auth.user.is_staff : Boolean(UFE.CURRENT_USER.is_staff)
  const editingUserRole = project ? models.project.getEditingUserRole(project, isUserStaff) : undefined
  const role = project !== undefined ? editingUserRole : undefined

  const displayMode = useDisplayMode(project)
  const isEditMode = displayMode === 'edit'
  const isPreviewMode = displayMode === 'preview'

  const matchesTablet = hooks.useDramaticallyInefficientMatchMediaQuery(plume.BREAKPOINTS.TABLET, true)

  const projectColor = models.project.backgroundColor(project) ?? plume.COLORS.BRAND_INDIGO
  const theme: Theme = {
    colors: {
      accent: plume.COLORS.hexToRgb(projectColor, 0.2),
      background: plume.COLORS.hexToRgb(projectColor, 0.1),
      hover: plume.COLORS.shade(projectColor, 1.3),
      primary: projectColor,
    },
  }

  return (
    <>
      <GlobalStyle />
      <Stickyfy
        height={60}
        config={stickyUntil == undefined ? { type: 'forever' } : { type: 'until', scrollTop: stickyUntil - 60 }}
        zindex={plume.ZINDEX.HEADER}
        top={0}
      >
        {isPreviewMode && (
          <PreviewBar
            previousEditionPath={prev ?? `/${project.slug}/`}
            translations={{ backToEdition: t('Back to page edition') }}
          />
        )}

        <header.Header lang={currentLocale} theme={theme} />
      </Stickyfy>
      {!matchesTablet && project && isEditMode && (
        <Stickyfy
          config={stickyUntil == undefined ? { type: 'forever' } : { type: 'until', scrollTop: stickyUntil - 113 }}
          height={53}
          zindex={plume.ZINDEX.HEADER - 1}
          top={60}
        >
          <TopFixedConfiguredNavMenu
            project={project as models.project.ProjectForOwner}
            role={role}
            screen={Screen.MY_PROJECT}
          />
        </Stickyfy>
      )}

      <shadow.TrackingProvider
        gtmContainerId={process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER || ''}
        pageCategory={shadow.CATEGORY.HOMEPAGE}
        userLocale={currentLocale}
      >
        <DisplayMode.Provider value={displayMode}>
          <SEO project={project} />
          <DoubleColumnWrapper>
            {matchesTablet && project && isEditMode && (
              <ConfiguredNavMenu
                project={project as models.project.ProjectForOwner}
                role={role}
                screen={Screen.MY_PROJECT}
              />
            )}
            <ThemeProvider theme={theme}>
              <ContentWrapper>{children}</ContentWrapper>
            </ThemeProvider>
          </DoubleColumnWrapper>
          <ThemeProvider theme={theme}>
            <InnerFooter enableReporting />
          </ThemeProvider>
          <layout.Footer navSections={layout.getNavSectionList(currentLocale)} lang={currentLocale} />
        </DisplayMode.Provider>
      </shadow.TrackingProvider>
    </>
  )
}

type StickyfyConfig =
  | {
      type: 'until'
      scrollTop: number
    }
  | {
      type: 'forever'
    }
type StickyfyProps = { children: React.ReactNode; height: number; top: number; zindex: number; config: StickyfyConfig }

function Stickyfy({ children, top, height, zindex, config }: StickyfyProps) {
  return (
    <HeaderStickyPositionner height={height} config={config} top={top} zindex={zindex}>
      <HeaderStickyContext config={config}>
        <HeaderWrapper top={top} zindex={zindex}>
          {children}
        </HeaderWrapper>
      </HeaderStickyContext>
    </HeaderStickyPositionner>
  )
}

const TopFixedConfiguredNavMenu = styled(ConfiguredNavMenu)`
  top: 0;
`

const HeaderStickyPositionner = styled.div<{ height: number; top: number; zindex: number; config: StickyfyConfig }>`
  height: ${({ height }) => height}px;
  ${({ config, top, zindex }) =>
    config.type === 'forever' &&
    css`
      position: sticky;
      top: ${top}px;
      z-index: ${zindex};
    `}
`

const HeaderStickyContext = styled.div<{ config: StickyfyConfig }>`
  ${({ config }) =>
    config.type === 'until' &&
    css`
      height: ${config.scrollTop}px;
      position: absolute;
      left: 0;
      right: 0;
    `}
`

const HeaderWrapper = styled.div<{ top: number; zindex: number }>`
  position: sticky;
  top: ${({ top }) => top}px;
  z-index: ${({ zindex }) => zindex};
`

const ContentWrapper = styled.div`
  border-left: 1px solid ${plume.COLORS.PRIMARY_SAND_100};
  width: 100%;
`

const DoubleColumnWrapper = styled.div<{ offsetTop?: number }>`
  display: flex;

  ${({ offsetTop = 0 }) => {
    if (offsetTop) {
      return css`
        padding-top: ${offsetTop}px; // NOTE: Account for the project nav menu
      `
    }
  }} @media screen and ${plume.BREAKPOINTS.TABLET} {
    padding-top: 0;
  }
`

const GlobalStyle = createGlobalStyle`
  html {
    scroll-behavior: smooth !important;
  }
`
